import {
  Order,
  OrderItem,
  FoodType,
  OrderFoodTypeStatus,
} from "@lemmonhq/types";

import { api } from "@app/data/api";

/**
 * Get the next status for an order based on current status
 */
export function getNextOrderStatus(currentStatus: string): string {
  switch (currentStatus.toLowerCase()) {
    case "new":
      return "preparing";
    case "hold":
      return "preparing";
    case "preparing":
      return "ready";
    case "ready":
      return "closed";
    default:
      return currentStatus;
  }
}

/**
 * Check if status can be changed
 */
export function canChangeStatus(currentStatus: string): boolean {
  return ["new", "hold", "preparing", "ready"].includes(
    currentStatus.toLowerCase(),
  );
}

/**
 * Get status button text for food type status
 */
export function getStatusButtonText(
  status: string,
  t: (key: string) => string,
): string {
  switch (status.toLowerCase()) {
    case "new":
      return t("orders.status.new");
    case "hold":
      return t("orders.status.hold");
    case "preparing":
      return t("orders.status.preparing");
    case "ready":
      return t("orders.status.ready");
    case "closed":
      return t("orders.status.closed");
    default:
      return status.toUpperCase();
  }
}

/**
 * Get overall order status button text
 */
export function getOrderStatusButtonText(
  status: string,
  t: (key: string) => string,
): string {
  switch (status.toLowerCase()) {
    case "new":
      return t("orders.status.new");
    case "preparing":
      return t("orders.status.preparing");
    case "ready":
      return t("orders.status.ready");
    case "closed":
      return t("orders.status.closed");
    default:
      return status.toUpperCase();
  }
}

/**
 * Group order items by food type
 */
export function groupOrderItemsByFoodType(
  items: OrderItem[],
  foodTypeStatuses: OrderFoodTypeStatus[] | null | undefined,
): Map<number | null, OrderItem[]> {
  return (items || []).reduce((acc, item) => {
    const matchingStatus = foodTypeStatuses?.find(
      (status) => status.id === item.food_type_id,
    );
    const key = matchingStatus ? matchingStatus.id : null;
    if (!acc.has(key)) {
      acc.set(key, []);
    }
    acc.get(key)!.push(item);
    return acc;
  }, new Map<number | null, OrderItem[]>());
}

/**
 * Get sorted food type list
 */
export function getSortedFoodTypesList(
  foodTypes: FoodType[],
  foodTypeStatuses: OrderFoodTypeStatus[] | null | undefined,
): (number | null)[] {
  const sortedFoodTypes = foodTypes
    .filter((foodType) =>
      foodTypeStatuses?.find((status) => status.id === foodType.id),
    )
    .sort((a, b) => a.order - b.order)
    .map((el) => el.id);
  return [...sortedFoodTypes, null];
}

/**
 * Format date for display
 */
export function formatOrderDate(dateString: string): {
  date: string;
  time: string;
} {
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString("fr-CH"),
    time: date.toLocaleTimeString("fr-CH", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }),
  };
}

/**
 * Update order status via API
 */
export async function updateOrderStatus(
  orderId: number,
  foodTypeId: number,
  currentStatus: string,
  onStatusChange: (
    orderId: number,
    foodTypeId: number,
    currentStatus: string,
  ) => Promise<void>,
): Promise<void> {
  await onStatusChange(orderId, foodTypeId, currentStatus);
}

/**
 * Cancel order via API
 */
export async function cancelOrder(
  orderId: number,
  currentStatus: string,
  onOrderCancel: (orderId: number) => void,
  t: (key: string) => string = (k) => k,
): Promise<boolean> {
  if (!window.confirm(t("orders.cancel.confirm"))) {
    return false;
  }

  try {
    const updatedOrders = await api.changeOrderStatus({
      type: "order",
      orderID: orderId,
      orderStatus: "canceled",
      foodTypeId: undefined,
      currentStatus: currentStatus,
    });

    if (updatedOrders) {
      onOrderCancel(orderId);
      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to cancel order:", error);
    throw error;
  }
}

/**
 * Get real order (handles parent relationships)
 */
export function getRealOrder(order: Order, orders: Order[]): Order {
  return order.parent_id
    ? orders.find((el) => el.id === order.parent_id) || order
    : order;
}
